import React from "react"
import { BuildingStorefrontIcon } from "@heroicons/react/24/outline"
import Value from "aldoo-ra/Value"
import useT from "../../hooks/useT"
import { groupBySeller, lineKey } from "../../checkout/cartItems"
import { useFreeShipping } from "./freeShipping"
import { useCartMinimums } from "./minimums"
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { formatMoney } from "../../account/ui"
import CartUpsell, { FreeDeliveryChip } from "./CartUpsell"

import { CART_VALIDATION_KEY } from "../../store/stockCheck"

export { CART_VALIDATION_KEY }

/**
 * Cart lines grouped by merchant (fragment "shopping-cart-product-list"):
 * a "Merchant: <seller>" header precedes each seller's lines, as the old cart
 * summary and the order mails do, with that seller's free delivery offer on
 * the right of it.
 */
export default function CartProductList({ store }) {
  const t = useT()
  const CartProduct = store.getFragment("shopping-cart-product")
  const [validation] = Value(CART_VALIDATION_KEY, null)
  const groups = groupBySeller(store.cart)
  const offers = useFreeShipping(store.cart) || []
  const minimums = useCartMinimums(store.cart) || []

  return (
    <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper flex flex-col px-5" data-testid="cart-lines">
      {groups.map((group) => {
        const offer = offers.find((entry) => entry.sellerName === group.name)
        // the seller's minimum order, not reached yet: said under its name; how much more, at the foot by the checkout
        const minimum = minimums.find((entry) => entry.sellerName === group.name && !entry.met)
        return (
          <div key={group.name || "unknown"} className="flex flex-col" data-testid="cart-seller-group">
            {group.name && (
              // the seller in one line: what more brings free delivery (Add €72.10 for free delivery from X), the
              // free delivery reached (its chip, the seller), or — no offer — the seller alone
              <div className="pt-4 border-b border-bordered pb-2" data-testid="cart-merchant">
                <div className="flex items-center gap-2 min-w-0 text-[14px] text-text_secondary">
                  <BuildingStorefrontIcon className="w-4 h-4 shrink-0" />
                  {offer && !offer.met ? (
                    <span className="min-w-0 truncate" data-testid="cart-free-shipping">
                      {t`Add`} <b className="font-bold text-text">{formatMoney(offer.remaining, offer.currency)}</b> {t`for free delivery from`} <span className="font-semibold text-text" data-testid="cart-merchant-name">{group.name}</span>
                    </span>
                  ) : (
                    <>
                      {offer?.met && <FreeDeliveryChip label={t`Free delivery`} />}
                      <span className="min-w-0 truncate font-semibold text-text" data-testid="cart-merchant-name">{group.name}</span>
                    </>
                  )}
                </div>
                {minimum && (
                  <div className="mt-1.5 flex items-start gap-1.5 text-[13px] text-error" data-testid="cart-min-order">
                    <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                      {t("Minimum order of %s", formatMoney(minimum.amount, minimum.currency))}
                    </span>
                  </div>
                )}
              </div>
            )}
            <div className="divide-y divide-bordered">
              {group.lines.map((line) => (
                <CartProduct key={lineKey(line)} product={line} store={store} issue={validation?.items?.find((item) => item.key === lineKey(line))} />
              ))}
            </div>
          </div>
        )
      })}
      {/* "Вземи изгодно": what goes with it, when the store offers it (STORE_CONFIG.upsell) */}
      {store.config?.upsell?.enabled && store.cart.length > 0 && <CartUpsell store={store} max={store.config.upsell.max || 3} />}
    </div>
  )
}
